import { useState, useEffect, useRef } from 'react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { ChevronDown } from 'lucide-react'
import { ListingScrapeDialog } from './ListingScrapeDialog'
import { knowledgeApiClient } from '@/lib/api/knowledge.client'
import { toast } from 'sonner'
import type { ListingUrlSyncConfig } from '@super/shared-types'
import { INTEGRATIONS } from '@super/shared-types'
import { integrationsClient } from '@/lib/api'
import { useAppAuth } from '@/contexts/app-auth.context'

interface SyncListingsDropdownProps {
  syncConfig: ListingUrlSyncConfig | null
  /** Connected integrations whose LISTINGS sync job is currently running. */
  activeListingsIntegration: string[]
  /** Connected integrations that support listings sync but have it stopped. */
  inactiveListingsIntegration?: string[]
  onSyncConfigChange: () => void
  isSyncing?: boolean
  onManualSyncStart?: () => void
}

export function SyncListingsDropdown({
  syncConfig,
  activeListingsIntegration,
  inactiveListingsIntegration = [],
  onSyncConfigChange,
  isSyncing = false,
  onManualSyncStart,
}: SyncListingsDropdownProps) {
  const { isSuperAdmin, currentCompany } = useAppAuth()
  const [showModifyDialog, setShowModifyDialog] = useState(false)
  const [showManualSyncDialog, setShowManualSyncDialog] = useState(false)
  const [showConflictDialog, setShowConflictDialog] = useState(false)
  const [showIntegrationSelectDialog, setShowIntegrationSelectDialog] =
    useState(false)
  const [conflictContext, setConflictContext] = useState<{
    type: 'scheduled' | 'manual'
    conflictingIntegrations?: Array<{ type: string; name: string }>
  } | null>(null)
  const [isRemoving, setIsRemoving] = useState(false)
  const isProceedingRef = useRef(false)
  const prevSyncConfigRef = useRef<ListingUrlSyncConfig | null>(syncConfig)

  const hasActiveIntegration = activeListingsIntegration.length > 0
  const hasConnectedIntegration =
    hasActiveIntegration || inactiveListingsIntegration.length > 0
  const showDropdown = hasConnectedIntegration || syncConfig !== null

  const handleAddSubmit = async (
    url: string,
    mode: 'delete' | 'append' | 'update',
  ) => {
    try {
      const result =
        isSuperAdmin && currentCompany?.id
          ? await knowledgeApiClient.createOrUpdateAdminListingUrlSync(
              currentCompany.id,
              url,
              mode,
            )
          : await knowledgeApiClient.createOrUpdateListingUrlSync(url, mode)

      if (!result) {
        toast.error('Failed to configure listing sync. Please try again.')
        return
      }

      if (result.success) {
        setShowModifyDialog(false)
        onSyncConfigChange()

        setTimeout(() => {
          toast.success('Sync is scheduled and will run every 6 hours', {
            duration: 5000,
          })
        }, 300)
      } else {
        const errorMessage =
          result?.message || 'Failed to configure listing sync'
        toast.error(errorMessage)
      }
    } catch (error: unknown) {
      console.error('Error configuring listing sync:', error)
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to configure listing sync'
      console.error('Full error details:', {
        error,
        isSuperAdmin,
        companyId: currentCompany?.id,
        url,
        mode,
      })
      toast.error(errorMessage)
    }
  }

  const handleSyncNow = () => {
    // Sync URL takes precedence over integrations
    if (syncConfig) {
      setShowManualSyncDialog(true)
      return
    }

    if (activeListingsIntegration.length > 0) {
      if (activeListingsIntegration.length > 1) {
        setShowIntegrationSelectDialog(true)
        return
      }

      // Sync from single integration
      proceedWithIntegrationSync(activeListingsIntegration[0])
      return
    }

    // No integration, no sync URL - open manual sync dialog
    setShowManualSyncDialog(true)
  }

  const handleManualSyncSubmit = async (
    url: string,
    mode: 'delete' | 'append' | 'update',
  ) => {
    if (syncConfig) {
      await proceedWithManualSync(url, mode)
      return
    }

    const hasScheduledSync = syncConfig !== null
    const hasPmsIntegration = activeListingsIntegration.length > 0

    if (hasScheduledSync || hasPmsIntegration) {
      setShowManualSyncDialog(false)
      setConflictContext({
        type: 'manual',
      })
      setShowConflictDialog(true)
      return
    }

    await proceedWithManualSync(url, mode)
  }

  const proceedWithManualSync = async (
    url: string,
    mode: 'delete' | 'append' | 'update',
  ) => {
    if (!currentCompany?.id) {
      toast.error('Company ID is required')
      return
    }

    try {
      // Use admin endpoint if super admin, otherwise use regular endpoint
      const result = isSuperAdmin
        ? await knowledgeApiClient.scrapeListingsForCompany(
            currentCompany.id,
            url,
            mode,
          )
        : await knowledgeApiClient.scrapeListings(url, mode)

      if (result?.success) {
        setShowManualSyncDialog(false)
        onManualSyncStart?.()
        onSyncConfigChange()

        setTimeout(() => {
          toast.success('Listing sync job started successfully', {
            description: `Job ID: ${result.jobId}. Polling for updates...`,
            duration: 5000,
          })
        }, 300)
      } else {
        const errorMessage =
          result?.message || 'Failed to start listing scraper job'
        toast.error(errorMessage)
      }
    } catch (error: unknown) {
      console.error('Error starting manual sync:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to sync listings',
      )
    }
  }

  const handleModifySettings = () => {
    // Check for conflicts with active integrations
    if (activeListingsIntegration.length > 0) {
      setConflictContext({
        type: 'scheduled',
        conflictingIntegrations: activeListingsIntegration.map(type => ({
          type,
          name: getIntegrationName(type),
        })),
      })
      setShowConflictDialog(true)
      return
    }

    // No conflicts, open modify dialog
    setShowModifyDialog(true)
  }

  const handleScheduleSync = () => {
    if (activeListingsIntegration.length > 0) {
      setConflictContext({
        type: 'scheduled',
        conflictingIntegrations: activeListingsIntegration.map(type => ({
          type,
          name: getIntegrationName(type),
        })),
      })
      setShowConflictDialog(true)
      return
    }

    setShowModifyDialog(true)
  }

  const handleIntegrationSelect = async (integrationType: string) => {
    setShowIntegrationSelectDialog(false)

    const hasScheduledSync = syncConfig !== null

    if (hasScheduledSync) {
      setConflictContext({ type: 'manual' })
      setShowConflictDialog(true)
      return
    }

    await proceedWithIntegrationSync(integrationType)
  }

  const proceedWithIntegrationSync = async (integrationType: string) => {
    try {
      let recordsProcessed = 0

      if (isSuperAdmin && currentCompany?.id) {
        // Try to use integration-specific client method if available
        if (
          integrationType === INTEGRATIONS.SHOWMOJO.TYPE &&
          integrationsClient.showMojo.syncAdminListings
        ) {
          const listings = await integrationsClient.showMojo.syncAdminListings(
            currentCompany.id,
          )
          recordsProcessed = listings.length
        } else {
          // Fall back to generic admin method
          const result =
            await integrationsClient.syncAdminListingsForIntegration(
              currentCompany.id,
              integrationType,
            )
          recordsProcessed = result.recordsProcessed || 0
        }
      } else {
        // Regular user - use generic method
        const result =
          await integrationsClient.syncListingsForIntegration(integrationType)
        recordsProcessed = result.recordsProcessed || 0
      }

      toast.success(
        `Successfully synced ${recordsProcessed} listing${recordsProcessed !== 1 ? 's' : ''}`,
        {
          description: `${getIntegrationName(integrationType)} listings have been updated`,
        },
      )

      onSyncConfigChange()
    } catch (error: unknown) {
      console.error('Error syncing listings from integration:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to sync listings from integration',
      )
    }
  }

  const handleStopListingSync = async (integrationType: string) => {
    const name = getIntegrationName(integrationType)
    if (
      !confirm(
        `Stop syncing listings from ${name}? The ${name} integration stays connected — only its listing sync is stopped, and you can start it again later.`,
      )
    ) {
      return
    }

    try {
      if (isSuperAdmin && currentCompany?.id) {
        await integrationsClient.stopAdminListingSync(
          currentCompany.id,
          integrationType,
        )
      } else {
        await integrationsClient.stopListingSync(integrationType)
      }

      toast.success(`${name} listing sync stopped`)
      onSyncConfigChange() // Refresh integration status (active → inactive)
    } catch (error: unknown) {
      console.error('Error stopping listing sync:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to stop ${name} listing sync`,
      )
    }
  }

  const handleStartListingSync = async (integrationType: string) => {
    const name = getIntegrationName(integrationType)
    try {
      if (isSuperAdmin && currentCompany?.id) {
        await integrationsClient.startAdminListingSync(
          currentCompany.id,
          integrationType,
        )
      } else {
        await integrationsClient.startListingSync(integrationType)
      }

      toast.success(`${name} listing sync started`)
      onSyncConfigChange()
    } catch (error: unknown) {
      console.error('Error starting listing sync:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : `Failed to start ${name} listing sync`,
      )
    }
  }

  const handleModifySubmit = async (
    url: string,
    mode: 'delete' | 'append' | 'update',
  ) => {
    try {
      const result =
        isSuperAdmin && currentCompany?.id
          ? await knowledgeApiClient.createOrUpdateAdminListingUrlSync(
              currentCompany.id,
              url,
              mode,
            )
          : await knowledgeApiClient.createOrUpdateListingUrlSync(url, mode)

      // Check if result is null (error case from withErrorHandling)
      if (!result) {
        toast.error('Failed to update sync settings. Please try again.')
        return
      }

      if (result.success) {
        setShowModifyDialog(false)
        onSyncConfigChange()

        setTimeout(() => {
          toast.success('Sync is scheduled and will run every 6 hours', {
            duration: 5000,
          })
        }, 300)
      } else {
        const errorMessage = result?.message || 'Failed to update sync settings'
        toast.error(errorMessage)
      }
    } catch (error: unknown) {
      console.error('Error updating sync settings:', error)
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update sync settings',
      )
    }
  }

  const handleConflictDialogClose = () => {
    setShowConflictDialog(false)
    setConflictContext(null)
  }

  // Close conflict dialog if sync config is removed (becomes null)
  // This handles the case where user removes sync while conflict dialog is open
  useEffect(() => {
    const prevSyncConfig = prevSyncConfigRef.current
    const wasRemoved = prevSyncConfig !== null && syncConfig === null

    console.log('SyncListingsDropdown - syncConfig changed:', {
      syncConfig,
      prevSyncConfig,
      wasRemoved,
      showConflictDialog,
      conflictContextType: conflictContext?.type,
      isProceeding: isProceedingRef.current,
    })

    // Only close if syncConfig was REMOVED (changed from non-null to null)
    // AND the dialog is open AND we're not intentionally proceeding
    if (wasRemoved && showConflictDialog && !isProceedingRef.current) {
      // Sync was removed, close the conflict dialog if it was blocking due to scheduled sync
      // But don't close if we're intentionally proceeding to open the schedule dialog
      if (
        conflictContext?.type === 'manual' ||
        conflictContext?.type === 'scheduled'
      ) {
        console.log('Closing conflict dialog because syncConfig was removed')
        setShowConflictDialog(false)
        setConflictContext(null)
      }
    }

    // Update the ref for next comparison
    prevSyncConfigRef.current = syncConfig
  }, [syncConfig, showConflictDialog, conflictContext])

  // Debug: Log when showModifyDialog changes
  useEffect(() => {
    console.log('showModifyDialog changed:', showModifyDialog)
  }, [showModifyDialog])

  const getIntegrationName = (integrationType: string): string => {
    // Resolve the display name for any known integration (e.g. RENTVINE ->
    // "Rentvine"); fall back to the raw type for anything unrecognized.
    const match = Object.values(INTEGRATIONS).find(
      config => config.TYPE === integrationType,
    )
    return match?.NAME ?? integrationType
  }

  const handleRemoveSync = async () => {
    if (!syncConfig) return

    if (
      !confirm(
        'Are you sure you want to remove the scheduled listing sync? This will stop automatic syncing.',
      )
    ) {
      return
    }

    try {
      setIsRemoving(true)
      const loadingToastId = toast.loading('Removing sync...')
      const result =
        isSuperAdmin && currentCompany?.id
          ? await knowledgeApiClient.removeAdminListingUrlSync(
              currentCompany.id,
            )
          : await knowledgeApiClient.removeListingUrlSync()
      toast.dismiss(loadingToastId)

      if (result?.success) {
        toast.success(result.message || 'Sync removed successfully')
        onSyncConfigChange()
      } else {
        const errorMessage = result?.message || 'Failed to remove sync'
        toast.error(errorMessage)
      }
    } catch (error: unknown) {
      toast.dismiss()
      console.error('Error removing sync:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to remove sync',
      )
    } finally {
      setIsRemoving(false)
    }
  }

  // Show simple button when no integrations and no sync URL
  if (!showDropdown) {
    return (
      <>
        <Button
          variant="form"
          onClick={handleScheduleSync}
          disabled={isSyncing || isRemoving}
        >
          Sync Listings
        </Button>

        <ListingScrapeDialog
          open={showModifyDialog}
          onOpenChange={setShowModifyDialog}
          onSubmit={handleAddSubmit}
          dialogMode="scheduled"
        />
        <Dialog
          open={showConflictDialog}
          onOpenChange={handleConflictDialogClose}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Conflict Detected</DialogTitle>
              <DialogDescription>
                Please review the warning below before scheduling a sync.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-4">
              {conflictContext?.type === 'scheduled' &&
                activeListingsIntegration.length > 0 && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
                    <div className="flex items-start gap-2">
                      <span className="text-amber-600 dark:text-amber-400">
                        (i)
                      </span>
                      <div>
                        <p className="font-medium text-amber-800 dark:text-amber-200">
                          By scheduling a sync, your listing integration
                          {activeListingsIntegration.length > 1
                            ? 's'
                            : ''} with{' '}
                          {activeListingsIntegration
                            .map(getIntegrationName)
                            .join(' and ')}{' '}
                          will be removed.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
            </div>
            <DialogFooter>
              {conflictContext?.type === 'scheduled' ? (
                <>
                  <Button variant="outline" onClick={handleConflictDialogClose}>
                    Cancel
                  </Button>
                  <Button
                    onClick={() => {
                      console.log(
                        'Proceed button clicked - opening schedule dialog',
                      )
                      isProceedingRef.current = true
                      setShowConflictDialog(false)
                      setConflictContext(null)
                      // Small delay to ensure conflict dialog closes before opening schedule dialog
                      setTimeout(() => {
                        console.log('Setting showModifyDialog to true')
                        setShowModifyDialog(true)
                        // Reset the ref after opening the dialog
                        setTimeout(() => {
                          isProceedingRef.current = false
                        }, 200)
                      }, 100)
                    }}
                  >
                    Proceed
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={handleConflictDialogClose}>
                  Close
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    )
  }

  // Show dropdown with options
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="form" disabled={isSyncing || isRemoving}>
            Sync Listings
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={handleSyncNow} disabled={isSyncing}>
            Sync Now
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={syncConfig ? handleModifySettings : handleScheduleSync}
            disabled={isSyncing || isRemoving}
          >
            {syncConfig ? 'Update listing url' : 'Schedule url sync'}
          </DropdownMenuItem>
          {syncConfig && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleRemoveSync}
                disabled={!syncConfig || isRemoving}
                className="text-red-600 focus:text-red-600 focus:bg-red-50"
              >
                Remove {syncConfig.syncUrl ? 'url sync' : 'scheduled sync'}
              </DropdownMenuItem>
            </>
          )}
          {hasConnectedIntegration && !syncConfig && (
            <>
              <DropdownMenuSeparator />
              {activeListingsIntegration.map(integrationType => (
                <DropdownMenuItem
                  key={`stop-${integrationType}`}
                  onClick={() => handleStopListingSync(integrationType)}
                  className="text-red-600 focus:text-red-600 focus:bg-red-50"
                >
                  Stop {getIntegrationName(integrationType)} sync
                </DropdownMenuItem>
              ))}
              {inactiveListingsIntegration.map(integrationType => (
                <DropdownMenuItem
                  key={`start-${integrationType}`}
                  onClick={() => handleStartListingSync(integrationType)}
                >
                  Start {getIntegrationName(integrationType)} sync
                </DropdownMenuItem>
              ))}
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Integration Selection Dialog */}
      <Dialog
        open={showIntegrationSelectDialog}
        onOpenChange={setShowIntegrationSelectDialog}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Select Integration to Sync</DialogTitle>
            <DialogDescription>
              Choose which integration to sync listings from
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {activeListingsIntegration.map(integrationType => (
              <Button
                key={integrationType}
                variant="outline"
                className="w-full justify-start"
                onClick={() => handleIntegrationSelect(integrationType)}
                disabled={isSyncing}
              >
                {getIntegrationName(integrationType)}
              </Button>
            ))}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowIntegrationSelectDialog(false)}
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ListingScrapeDialog
        open={showModifyDialog}
        onOpenChange={setShowModifyDialog}
        onSubmit={syncConfig ? handleModifySubmit : handleAddSubmit}
        initialUrl={syncConfig?.syncUrl}
        initialMode={syncConfig?.mode}
        dialogMode="scheduled"
      />
      <ListingScrapeDialog
        open={showManualSyncDialog}
        onOpenChange={setShowManualSyncDialog}
        onSubmit={handleManualSyncSubmit}
        initialUrl={syncConfig?.syncUrl}
        dialogMode="manual"
      />
      <Dialog
        open={showConflictDialog}
        onOpenChange={handleConflictDialogClose}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conflict Detected</DialogTitle>
            <DialogDescription>
              {conflictContext?.type === 'manual'
                ? 'A conflict was detected before starting manual sync.'
                : 'Please review the warning below before scheduling a sync.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-4">
            {conflictContext?.type === 'manual' && (
              <div className="text-sm">
                <p className="font-medium text-amber-600 dark:text-amber-500">
                  Cannot proceed with manual sync
                </p>
                <p className="text-muted-foreground mt-1">
                  Please disable or disconnect the active syncs below before
                  performing a manual sync. This prevents data conflicts and
                  ensures your listings remain consistent.
                </p>
              </div>
            )}
            {conflictContext?.type === 'scheduled' &&
              activeListingsIntegration.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950">
                  <div className="flex items-start gap-2">
                    <span className="text-amber-600 dark:text-amber-400">
                      (i)
                    </span>
                    <div>
                      <p className="font-medium text-amber-800 dark:text-amber-200">
                        By scheduling a sync, your listing integration
                        {activeListingsIntegration.length > 1
                          ? 's'
                          : ''} with{' '}
                        {activeListingsIntegration
                          .map(getIntegrationName)
                          .join(' and ')}{' '}
                        will be removed.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            {syncConfig && (
              <div className="text-sm border-l-2 border-amber-500 pl-3">
                <p className="font-medium">Scheduled Sync Active</p>
                <p className="text-muted-foreground">
                  A scheduled sync is currently configured and will continue to
                  run automatically.
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Action required: Remove the scheduled sync from the dropdown
                  menu before performing a manual sync.
                </p>
              </div>
            )}
            {activeListingsIntegration.length > 0 && (
              <div className="text-sm border-l-2 border-amber-500 pl-3">
                <p className="font-medium">PMS Integration Active</p>
                <p className="text-muted-foreground">
                  {activeListingsIntegration.length === 1
                    ? `A PMS integration (${getIntegrationName(activeListingsIntegration[0])}) with listing sync is currently active and will continue to sync listings automatically.`
                    : `${activeListingsIntegration.length} PMS integrations with listing sync are currently active: ${activeListingsIntegration.map(getIntegrationName).join(', ')}`}
                </p>
                <p className="text-muted-foreground mt-1 text-xs">
                  Action required: Disconnect the integration
                  {activeListingsIntegration.length > 1 ? 's' : ''} from
                  Settings → Integrations before proceeding.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            {conflictContext?.type === 'scheduled' ? (
              <>
                <Button variant="outline" onClick={handleConflictDialogClose}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    console.log(
                      'Proceed button clicked (simple button case) - opening schedule dialog',
                    )
                    isProceedingRef.current = true
                    setShowConflictDialog(false)
                    setConflictContext(null)
                    // Small delay to ensure conflict dialog closes before opening schedule dialog
                    setTimeout(() => {
                      console.log(
                        'Setting showModifyDialog to true (simple button case)',
                      )
                      setShowModifyDialog(true)
                      // Reset the ref after opening the dialog
                      setTimeout(() => {
                        isProceedingRef.current = false
                      }, 200)
                    }, 100)
                  }}
                >
                  Proceed
                </Button>
              </>
            ) : (
              <Button onClick={handleConflictDialogClose}>Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
